interface Rect {
  x: number;
  y: number;
  width: number;
  height: number;
}

interface Size {
  width: number;
  height: number;
}

interface Transform {
  scale: number;
  positionX: number;
  positionY: number;
}

const getViewportRect = ({ scale, positionX, positionY }: Transform, wrapper: Size): Rect => ({
  x: -positionX / scale,
  y: -positionY / scale,
  width: wrapper.width / scale,
  height: wrapper.height / scale,
});

const getVisibleHeight = (item: Pick<Rect, "y" | "height">, viewport: Pick<Rect, "y" | "height">): number =>
  Math.max(0, Math.min(item.y + item.height, viewport.y + viewport.height) - Math.max(item.y, viewport.y));

const getFocusedIndex = (items: Pick<Rect, "y" | "height">[], viewport: Pick<Rect, "y" | "height">): number => {
  const viewportCenter = viewport.y + viewport.height / 2;
  let focusedIndex = -1;
  let largestVisibleHeight = -1;
  let smallestDistance = Infinity;
  items.forEach((item, index) => {
    const visibleHeight = getVisibleHeight(item, viewport);
    const distance = Math.abs(item.y + item.height / 2 - viewportCenter);
    if (
      visibleHeight > largestVisibleHeight ||
      (visibleHeight === largestVisibleHeight && distance < smallestDistance)
    ) {
      largestVisibleHeight = visibleHeight;
      smallestDistance = distance;
      focusedIndex = index;
    }
  });
  return focusedIndex;
};

const FULLY_VISIBLE_TOLERANCE = 1;

const isFullyVisible = (item: Pick<Rect, "y" | "height">, viewport: Pick<Rect, "y" | "height">): boolean =>
  getVisibleHeight(item, viewport) >= item.height - FULLY_VISIBLE_TOLERANCE;

interface NavigationTargets {
  previous?: number;
  next?: number;
}

const getNavigationTargets = (
  items: Pick<Rect, "y" | "height">[],
  viewport: Pick<Rect, "y" | "height">,
): NavigationTargets => {
  const focusedIndex = getFocusedIndex(items, viewport);
  const isHidden = (index: number) => {
    const item = items[index];
    return item !== undefined && !isFullyVisible(item, viewport);
  };
  const indexes = items.map((_, index) => index);
  const previous = indexes.filter(index => index < focusedIndex && isHidden(index)).at(-1);
  const next = indexes.find(index => index > focusedIndex && isHidden(index));
  return { previous, next };
};

export { getFocusedIndex, getNavigationTargets, getViewportRect };
export type { Rect, Size, Transform };
